import { PLAN_LIMITS, resolvePlanTierKey, type PlanTier } from '../constants/pricingPlans';
import { PLAN_UPGRADE_CONTACT_EMAIL, SUBSCRIPTION_PLANS_ENABLED } from '../constants/subscriptionPlans';

export type GatedFeature = 'complianceHub' | 'brsrExports' | 'greenFinance' | 'carbonCredits';

const FEATURE_LABELS: Record<GatedFeature, string> = {
  complianceHub: 'Compliance hub',
  brsrExports: 'BRSR-oriented exports',
  greenFinance: 'Green finance matching',
  carbonCredits: 'Carbon credits workspace',
};

const TIER_ORDER: PlanTier[] = ['free', 'starter', 'core', 'growth', 'scale'];

const TIER_LABELS: Record<PlanTier, string> = {
  free: 'Free',
  starter: 'Starter',
  core: 'Core',
  growth: 'Growth',
  scale: 'Scale',
};

/** Resolve effective tier from billing profile or default to free. */
export function resolveEffectivePlanTier(
  selectedPlanId?: string | null,
  status?: string,
  paidUntil?: string | null
): PlanTier {
  if (!selectedPlanId || status !== 'paid') {
    return 'free';
  }
  if (paidUntil && new Date(paidUntil).getTime() < Date.now()) {
    return 'free';
  }
  return resolvePlanTierKey(selectedPlanId);
}

export function planHasFeature(tier: PlanTier, feature: GatedFeature): boolean {
  if (!SUBSCRIPTION_PLANS_ENABLED) {
    return true;
  }
  return Boolean(PLAN_LIMITS[tier][feature]);
}

export function getUpgradeMessage(feature: GatedFeature, currentTier: PlanTier): string {
  if (!SUBSCRIPTION_PLANS_ENABLED) {
    return '';
  }
  const label = FEATURE_LABELS[feature];
  if (planHasFeature(currentTier, feature)) {
    return '';
  }
  return `${label} is available on the Growth plan and above. You are on ${TIER_LABELS[currentTier]}. Contact ${PLAN_UPGRADE_CONTACT_EMAIL} to upgrade.`;
}

const nextDocumentTier = (tier: PlanTier): PlanTier | null => {
  const currentIndex = TIER_ORDER.indexOf(tier);
  return TIER_ORDER.slice(currentIndex + 1).find((candidate) => (
    PLAN_LIMITS[candidate].documentsPerMonth > PLAN_LIMITS[tier].documentsPerMonth
  )) ?? null;
};

export function getDocumentLimitWarning(
  tier: PlanTier,
  documentCount: number
): { atLimit: boolean; message: string } | null {
  if (!SUBSCRIPTION_PLANS_ENABLED) {
    return null;
  }
  const limit = PLAN_LIMITS[tier].documentsPerMonth;
  if (documentCount < limit * 0.85) {
    return null;
  }
  const atLimit = documentCount >= limit;
  const upgradeTier = nextDocumentTier(tier);
  const atLimitMessage = upgradeTier
    ? `You have reached your ${limit} documents/month limit on the ${TIER_LABELS[tier]} plan. Contact ${PLAN_UPGRADE_CONTACT_EMAIL} to upgrade to ${TIER_LABELS[upgradeTier]} (${PLAN_LIMITS[upgradeTier].documentsPerMonth} docs/mo) or pay ₹12 per extra document.`
    : `You have reached your ${limit} documents/month limit on the ${TIER_LABELS[tier]} plan. Contact ${PLAN_UPGRADE_CONTACT_EMAIL} for higher-volume pricing or pay ₹12 per extra document.`;
  return {
    atLimit,
    message: atLimit
      ? atLimitMessage
      : `You have used ${documentCount} of ${limit} documents this month. Consider upgrading before you hit the limit.`,
  };
}

export const GROWTH_PLAN_ID = 'msme_growth_monthly';
